import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalculateIcon from '@mui/icons-material/Calculate';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SendIcon from '@mui/icons-material/Send';
import ReplayIcon from '@mui/icons-material/Replay';
import { PageProps } from '../../../components/types/coretypes';
import { FGrid, FGridMoneyCell } from '../grid';
import { FWorkbenchShell } from '../workbench';
import DeterminantReportPanel from './DeterminantReportPanel';
import {
  acceptDeterminantBudget,
  fetchDeterminantBudgetWorkbench,
  fetchReportTable,
  initializeFromRecommendation,
  recomputeCapacity,
  regenerateReportTables,
  releasePlanningCapacity,
  returnDeterminantBudget,
  saveManagementAdjustment,
  validateDeterminantBudget,
} from './determinantBudget.apis';
import {
  DeterminantBudgetTabCode,
  IDeterminantBudgetWorkbench,
  IReportTablePayload,
} from './determinantBudget.models';
import { PlanningPageProps, PlanningWorkbenchShell } from '../planningcore';
import { fetchPlanningJourneyPhases } from '../core/planningJourney.cache';
import FPlanningJourney from '../core/FPlanningJourney';

type State = {
  tab: DeterminantBudgetTabCode;
  loading: boolean;
  error: string;
  success: string;
  warning: string;
  phases: any[];
  workbench: IDeterminantBudgetWorkbench | null;
  reportTable: IReportTablePayload | null;
  adjustmenttype: string;
  adjustmentamount: string;
  adjustmentreason: string;
  authoritycode: string;
  notes: string;
};

class DeterminantBudgetWorkbench extends React.Component<PlanningPageProps, State> {
  state: State = {
    tab: 'summary',
    loading: false,
    error: '',
    success: '',
    warning: '',
    phases: [],
    workbench: null,
    reportTable: null,
    adjustmenttype: 'INCREASE',
    adjustmentamount: '0',
    adjustmentreason: '',
    authoritycode: '',
    notes: '',
  };

  componentDidMount() {
    this.initialize();
  }

  private getPageParam = (key: string): unknown => {
    const params: any = this.props.params ?? {};
    return params[key] ?? params?.get?.(key) ?? undefined;
  };

  private getPlanningCycleId = (): number => {
    //const p = (this.props.params || {}) as any;
    //return Number(p.planningcycleid ?? p.pci ?? p.planningCycleId ?? 0);
    const value = this.props.plancontext?.pci;
    return Number(value || 0);
  };

  private initialize = async () => {
    const planningcycleid = this.getPlanningCycleId();

    if (!planningcycleid) {
      this.setState({
        error: 'Planning Cycle context is missing. Please set your preferred context in the Planning Setup ',
      });
      return;
    }

    this.setState({ loading: true, error: '', success: '' });

    try {
      const [phases] = await Promise.all([fetchPlanningJourneyPhases()]);
      this.setState({ phases });
      await this.open();
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private p = () => (this.props.params as any) || {};

  private budgetId = () =>
    Number(this.p().determinantbudgetid ?? this.p().dbi ?? 0);

  private recommendationId = () =>
    Number(this.p().recommendationid ?? this.p().rci ?? 0);

  private setParam = (k: string, v: any) => this.props.onParam('set')(k, v);

  private mutate = async (message: string, fn: () => Promise<any>) => {
    this.setState({ loading: true, error: '', success: '', warning: '' });
    try {
      await fn();
      this.setState({ success: message });
      await this.getWorkbench(true);
    } catch (e: any) {
      this.setState({ error: e?.message || String(e), loading: false });
    }
  };

  open = async () => {
    let rid = this.budgetId();
    const rci = this.recommendationId();
    if (!rid && rci) {
      this.setState({ loading: true });
      try {
        const r = await initializeFromRecommendation(rci);
        rid = Number(r?.rid || 0);
        if (rid) this.setParam('determinantbudgetid', rid);
        if (r?.rix) this.setParam('determinantbudgetix', r.rix);
      } catch (e: any) {
        this.setState({ error: e?.message || String(e), loading: false });
        return;
      }
    }
    await this.getWorkbench();
  };

  getWorkbench = async (preserve = false) => {
    const rid = this.budgetId();
    if (!rid) {
      this.setState({
        error:
          'A certified Executive Recommendation is required. Reopen Determinant Budget from Executive Review.',
      });
      return;
    }
    this.setState({
      loading: true,
      error: '',
      success: preserve ? this.state.success : '',
      warning: '',
    });
    try {
      const w = await fetchDeterminantBudgetWorkbench(
        rid,
        this.recommendationId() || null
      );
      this.setState({
        workbench: w,
        tab: this.state.workbench ? this.state.tab : w.activeTab || 'summary',
      });
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ loading: false });
    }
  };

  loadTable = async (code: string) => {
    const w = this.state.workbench;
    if (!w) return;
    this.setState({ loading: true, error: '' });
    try {
      this.setState({
        reportTable: await fetchReportTable(
          w.context.determinantbudgetid,
          code
        ),
      });
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ loading: false });
    }
  };

  recompute = () =>
    this.mutate('Initial planning capacity recomputed.', () =>
      recomputeCapacity(
        this.state.workbench!.context.determinantbudgetid,
        this.state.workbench!.context.stamp
      )
    );

  saveAdjustment = async () => {
    if (!this.state.adjustmentreason.trim()) {
      this.setState({ warning: 'A management adjustment reason is required.' });
      return;
    }
    const w = this.state.workbench!;
    await this.mutate('Management adjustment recorded.', () =>
      saveManagementAdjustment({
        determinantbudgetid: w.context.determinantbudgetid,
        adjustmenttype: this.state.adjustmenttype,
        amount: Number(this.state.adjustmentamount || 0),
        reason: this.state.adjustmentreason.trim(),
        authoritycode: this.state.authoritycode || undefined,
        stamp: w.context.stamp,
      })
    );
    this.setState({ adjustmentamount: '0', adjustmentreason: '' });
  };

  regenerate = (codes?: string[]) =>
    this.mutate(
      codes?.length
        ? 'Selected consolidated table regenerated.'
        : 'Consolidated Report Tables 4–18 regenerated.',
      () =>
        regenerateReportTables(
          this.state.workbench!.context.determinantbudgetid,
          codes
        )
    );

  validate = () =>
    this.mutate('Determinant Budget validation completed.', () =>
      validateDeterminantBudget(
        this.state.workbench!.context.determinantbudgetid
      )
    );

  accept = () =>
    this.mutate('Determinant Budget accepted for release.', () =>
      acceptDeterminantBudget(
        this.state.workbench!.context.determinantbudgetid,
        this.state.notes
      )
    );

  returnBudget = () =>
    this.mutate('Determinant Budget returned to Executive Review.', () =>
      returnDeterminantBudget(
        this.state.workbench!.context.determinantbudgetid,
        this.state.notes
      )
    );

  release = async () => {
    const w = this.state.workbench!;
    await this.mutate('Planning capacity released.', () =>
      releasePlanningCapacity(w.context.determinantbudgetid, this.state.notes)
    );
    const next = w.actions.nextpage || 'planning';
    this.setParam('planningcycleid', w.context.planningcycleid);
    this.setParam('determinantbudgetid', w.context.determinantbudgetid);
    this.setParam('capacityversion', w.context.capacityversion);
    this.props.setPage(next);
  };

  renderContext() {
    const c = this.state.workbench?.context;
    if (!c) return null;
    return (
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Typography variant="caption">Planning Cycle</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {c.planningcyclename || c.planningcyclecode}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Typography variant="caption">Certified Recommendation</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {c.recommendationcode ||
              c.recommendationix ||
              `#${c.recommendationid}`}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Typography variant="caption">Recommended Scenario</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {c.scenariocode} — {c.scenarioname}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6, md: 1.5 }}>
          <Chip size="small" label={c.statecode} />
        </Grid>
        <Grid size={{ xs: 6, md: 1.5 }}>
          <Chip
            size="small"
            variant="outlined"
            label={`Capacity v${c.capacityversion}`}
          />
        </Grid>
      </Grid>
    );
  }

  money = (v: any) =>
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(
      Number(v || 0)
    );

  summary() {
    const w = this.state.workbench!;
    const cards = [
      ['Recommended Scenario', w.context.scenariocode],
      ['Economic Capacity', this.money(w.capacity.computedeconomiccapacity)],
      ['Planning Capacity', this.money(w.capacity.computedplanningcapacity)],
      ['Planning Envelope', this.money(w.capacity.planningenvelope)],
      ['Report Completeness', `${w.report.completeness || 0}%`],
      [
        'Validation',
        w.validation.ready ? 'READY' : `${w.validation.blockingcount} blocking`,
      ],
    ];
    return (
      <Grid container spacing={2}>
        {cards.map(([l, v]) => (
          <Grid key={String(l)} size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="caption" color="text.secondary">
                {l}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {v}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  }

  envelope() {
    const c = this.state.workbench!.capacity;
    const rows = [
      {
        id: 1,
        label: 'Computed Economic Capacity',
        amount: c.computedeconomiccapacity,
      },
      {
        id: 2,
        label: 'Computed Planning Capacity',
        amount: c.computedplanningcapacity,
      },
      { id: 3, label: 'Approved Adjustments', amount: c.approvedadjustment },
      { id: 4, label: 'Management Reserve', amount: c.managementreserve },
      { id: 5, label: 'Planning Envelope', amount: c.planningenvelope },
      { id: 6, label: 'Remaining Capacity', amount: c.remainingcapacity || 0 },
    ];
    return (
      <Stack spacing={2}>
        <Alert severity={c.stale ? 'warning' : 'info'}>
          {c.stale
            ? 'Capacity is stale and must be recomputed.'
            : 'Capacity is current for the certified recommendation.'}
        </Alert>
        <FGrid
          title="Budget Envelope"
          rows={rows}
          columns={[
            { field: 'label', headerName: 'Measure', flex: 1, minWidth: 280 },
            {
              field: 'amount',
              headerName: 'Amount',
              width: 220,
              renderCell: FGridMoneyCell,
            },
          ]}
          getRowId={(r: any) => r.id}
          height={420}
        />
        <Button
          variant="outlined"
          startIcon={<CalculateIcon />}
          disabled={
            !this.state.workbench!.actions.canRecompute || this.state.loading
          }
          onClick={this.recompute}
        >
          Recompute Capacity
        </Button>
      </Stack>
    );
  }

  capacity() {
    return (
      <FGrid
        title="Capacity Analysis"
        rows={this.state.workbench!.capacitylines || []}
        columns={[
          { field: 'code', headerName: 'Code', width: 150 },
          {
            field: 'name',
            headerName: 'Capacity Component',
            flex: 1,
            minWidth: 260,
          },
          {
            field: 'sourceamount',
            headerName: 'Source',
            width: 160,
            renderCell: FGridMoneyCell,
          },
          {
            field: 'computedamount',
            headerName: 'Computed',
            width: 160,
            renderCell: FGridMoneyCell,
          },
          {
            field: 'adjustedamount',
            headerName: 'Adjusted',
            width: 160,
            renderCell: FGridMoneyCell,
          },
          {
            field: 'finalamount',
            headerName: 'Final',
            width: 160,
            renderCell: FGridMoneyCell,
          },
        ]}
        getRowId={(r: any) => r.rid}
        height={520}
      />
    );
  }

  management() {
    const w = this.state.workbench!;
    return (
      <Stack spacing={2}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              select
              label="Adjustment Type"
              value={this.state.adjustmenttype}
              onChange={(e) =>
                this.setState({ adjustmenttype: e.target.value })
              }
              fullWidth
            >
              <MenuItem value="INCREASE">Increase Capacity</MenuItem>
              <MenuItem value="DECREASE">Decrease Capacity</MenuItem>
              <MenuItem value="RESERVE">Management Reserve</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              label="Amount"
              type="number"
              value={this.state.adjustmentamount}
              onChange={(e) =>
                this.setState({ adjustmentamount: e.target.value })
              }
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              label="Authority Code"
              value={this.state.authoritycode}
              onChange={(e) => this.setState({ authoritycode: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Button
              sx={{ height: '56px' }}
              fullWidth
              variant="contained"
              disabled={!w.actions.canAdjust || this.state.loading}
              onClick={this.saveAdjustment}
            >
              Record Adjustment
            </Button>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Adjustment Reason"
              value={this.state.adjustmentreason}
              onChange={(e) =>
                this.setState({ adjustmentreason: e.target.value })
              }
              multiline
              minRows={3}
              fullWidth
            />
          </Grid>
        </Grid>
        <FGrid
          title="Adjustment History"
          rows={w.adjustments || []}
          columns={[
            { field: 'adjustmenttype', headerName: 'Type', width: 140 },
            {
              field: 'amount',
              headerName: 'Amount',
              width: 160,
              renderCell: FGridMoneyCell,
            },
            { field: 'reason', headerName: 'Reason', flex: 1, minWidth: 260 },
            { field: 'authorityname', headerName: 'Authority', width: 180 },
            { field: 'approvedby', headerName: 'Approved By', width: 180 },
            { field: 'approvedat', headerName: 'Date', width: 180 },
          ]}
          getRowId={(r: any) => r.rid}
          height={380}
        />
      </Stack>
    );
  }

  validation() {
    const v = this.state.workbench!.validation;
    return (
      <Stack spacing={1.5}>
        <Alert severity={v.ready ? 'success' : v.stale ? 'warning' : 'error'}>
          Readiness score: {v.score || 0}% — {v.blockingcount || 0} blocking,{' '}
          {v.warningcount || 0} warnings.
        </Alert>
        {(v.items || []).map((x) => (
          <Alert
            key={x.code}
            severity={
              x.passed
                ? 'success'
                : x.severity === 'BLOCKING'
                  ? 'error'
                  : x.severity === 'WARNING'
                    ? 'warning'
                    : 'info'
            }
          >
            {x.label}
            {x.message ? `: ${x.message}` : ''}
          </Alert>
        ))}
        <Button
          variant="contained"
          startIcon={<FactCheckIcon />}
          disabled={
            !this.state.workbench!.actions.canValidate || this.state.loading
          }
          onClick={this.validate}
        >
          Run Validation
        </Button>
      </Stack>
    );
  }

  releaseTab() {
    const w = this.state.workbench!;
    return (
      <Stack spacing={2}>
        <Alert severity={w.actions.canRelease ? 'success' : 'warning'}>
          {w.actions.canRelease
            ? 'All release gates are satisfied.'
            : 'Release requires a current validation with no blocking issues and complete Tables 4–18.'}
        </Alert>
        <TextField
          label="Management / Release Notes"
          value={this.state.notes}
          onChange={(e) => this.setState({ notes: e.target.value })}
          multiline
          minRows={4}
          fullWidth
        />
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="contained"
            color="success"
            disabled={!w.actions.canAccept || this.state.loading}
            onClick={this.accept}
          >
            Accept Determinant Budget
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            disabled={!w.actions.canRelease || this.state.loading}
            onClick={this.release}
          >
            Release Planning Capacity
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<ReplayIcon />}
            disabled={
              !w.actions.canReturn ||
              this.state.loading ||
              !this.state.notes.trim()
            }
            onClick={this.returnBudget}
          >
            Return to Executive Review
          </Button>
        </Stack>
      </Stack>
    );
  }

  renderTab() {
    const w = this.state.workbench;
    if (!w) return null;
    switch (this.state.tab) {
      case 'envelope':
        return this.envelope();
      case 'capacity':
        return this.capacity();
      case 'management':
        return this.management();
      case 'validation':
        return this.validation();
      case 'release':
        return this.releaseTab();
      case 'report':
        return (
          <DeterminantReportPanel
            report={w.report}
            payload={this.state.reportTable}
            loading={this.state.loading}
            canRegenerate={w.actions.canRegenerateTables}
            onLoad={this.loadTable}
            onRegenerate={this.regenerate}
          />
        );
      default:
        return (
          <Stack spacing={2}>
            {this.summary()}
            <Alert severity="info">
              The Determinant Budget consumes the active certified Executive
              Recommendation, computes initial planning capacity and generates
              Consolidated Report Tables 4–18.
            </Alert>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Recommendation provenance
              </Typography>
              <Typography>
                {w.recommendation.reason || 'No rationale supplied.'}
              </Typography>
              {w.recommendation.conditions && (
                <Typography color="text.secondary">
                  Conditions: {w.recommendation.conditions}
                </Typography>
              )}
            </Paper>
          </Stack>
        );
    }
  }
  
  render() {
    const w = this.state.workbench;
    return (
      <PlanningWorkbenchShell
        {...this.props}
        onBack={this.props.onBack}
        headerExtension={
          <FPlanningJourney
            phases={this.state.phases}
            //currentPhaseCode={w?.context.phasecode || 'DETERMINANT_BUDGET'}
            currentPhaseCode={'DETERMINANT_BUDGET'}
          />
        }
        
        title="BIE Determinant Budget"
        subtitle="Initial planning capacity and Consolidated Report Tables 4–18"
        icon={<AccountBalanceIcon />}
        loading={this.state.loading}
        onRefresh={this.getWorkbench}
        //context={this.renderContext()}
      >
        <Stack spacing={2}>
          {this.state.error && (
            <Alert severity="error">{this.state.error}</Alert>
          )}
          {this.state.success && (
            <Alert severity="success">{this.state.success}</Alert>
          )}
          {this.state.warning && (
            <Alert severity="warning">{this.state.warning}</Alert>
          )}
          {w && (
            <Tabs
              value={this.state.tab}
              onChange={(_, tab) => this.setState({ tab })}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab value="summary" label="Executive Summary" />
              <Tab value="envelope" label="Budget Envelope" />
              <Tab value="capacity" label="Capacity Analysis" />
              <Tab value="management" label="Management Review" />
              <Tab
                value="validation"
                label={`Validation (${w.validation.blockingcount})`}
              />
              <Tab value="release" label="Release" />
              <Tab value="report" label="Consolidated Report" />
            </Tabs>
          )}
          <Box>{this.renderTab()}</Box>
        </Stack>
      </PlanningWorkbenchShell>
    );
  }
}
export default DeterminantBudgetWorkbench;
